import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AlertCircle, Search, Trash2, ShieldAlert, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only diagnostic + recovery tool for stuck user accounts.
 *
 * Use when a user cannot log in ("invalid credentials") AND cannot be recreated
 * ("email already exists") — a classic mixed-case-email or duplicate-record
 * situation. This panel lets the admin:
 *   1. Search any email (case-insensitive substring)
 *   2. See ALL matching records + their status, password_hash_present, roles
 *   3. Force-delete ALL records for that email in one shot (so a fresh
 *      "Create User" then works)
 */
const UserDiagnosticTool = ({ onPurged }) => {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [purging, setPurging] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
  });

  const handleLookup = async () => {
    const q = email.trim();
    if (q.length < 2) { toast.error('Enter at least 2 characters'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/users/lookup?email=${encodeURIComponent(q)}`, authHeaders());
      setResults(res.data);
      if (res.data.count === 0) toast.info('No matching users found.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async (targetEmail) => {
    const e = (targetEmail || email).trim();
    if (!window.confirm(
      `Force-delete ALL user records matching "${e}" (case-insensitive)?\n\n` +
      `This cascades to delete:\n` +
      `  • The user(s)\n` +
      `  • All applications submitted under that email\n` +
      `  • All messages and documents from that user\n\n` +
      `Use this only to recover a stuck account. This cannot be undone.`
    )) return;
    setPurging(true);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/users/purge-by-email?email=${encodeURIComponent(e)}&confirm=true`,
        authHeaders()
      );
      toast.success(`Purged ${res.data.purged} record(s). You can now recreate the user with this email.`);
      setResults({ count: 0, users: [] });
      onPurged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="user-diagnostic-tool">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full"
          data-testid="diagnostic-tool-toggle"
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <span>User Account Diagnostic & Recovery</span>
          </CardTitle>
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        </button>
        {!expanded && (
          <p className="text-xs text-slate-500 mt-1.5">
            Stuck account? Can&apos;t log in but can&apos;t recreate either? Click to expand.
          </p>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          <div className="bg-amber-100/70 border border-amber-300 rounded-lg p-3 flex gap-2 items-start">
            <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-900">
              <strong>When to use:</strong> a user reports &quot;Invalid credentials&quot; even with the right password,
              and the admin can&apos;t delete-and-recreate them (&quot;email already exists&quot;).
              Usually caused by mixed-case emails or duplicate legacy records.
              Look up, then purge ALL records matching the email and recreate fresh.
            </p>
          </div>

          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Email to look up (e.g. dan@living2-0.com)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              className="flex-1"
              data-testid="diagnostic-email-input"
            />
            <Button
              onClick={handleLookup}
              disabled={loading}
              className="bg-[#0E333F] hover:bg-[#072028] text-white"
              data-testid="diagnostic-lookup-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-1.5">Look Up</span>
            </Button>
          </div>

          {results && (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">
                Found <strong>{results.count}</strong> record(s).
              </p>
              {results.users.map((u) => (
                <div key={u.id} className="bg-white border border-slate-200 rounded-lg p-3 text-sm" data-testid={`diagnostic-user-${u.id}`}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">Name</div>
                      <div className="font-medium text-slate-800">{u.name || <span className="text-slate-400 italic">(none)</span>}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">Email (stored)</div>
                      <div className="font-mono text-xs text-slate-800 break-all">{u.email}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">Status</div>
                      <div className={`font-semibold ${u.status === 'approved' ? 'text-emerald-700' : u.status === 'pending' ? 'text-amber-700' : 'text-red-700'}`}>{u.status}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">Password Hash</div>
                      <div className={u.password_hash_present ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                        {u.password_hash_present ? 'Present ✓' : 'MISSING ✗'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2 text-xs text-slate-600">
                    <div><span className="text-slate-500">Roles:</span> <span className="font-medium">{(u.roles || []).join(', ') || '(none)'}</span></div>
                    <div><span className="text-slate-500">Created:</span> <span className="font-medium">{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</span></div>
                    <div><span className="text-slate-500">By:</span> <span className="font-medium">{u.created_by_name || u.created_by || '-'}</span></div>
                    <div><span className="text-slate-500">Last Login:</span> <span className="font-medium">{u.last_login ? new Date(u.last_login).toLocaleString() : '-'}</span></div>
                  </div>
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => handlePurge(u.email)}
                      disabled={purging}
                      data-testid={`diagnostic-purge-${u.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Force-Delete this email (cascade)
                    </Button>
                  </div>
                </div>
              ))}
              {results.count > 1 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-800">
                    <strong>{results.count} duplicate records found</strong> for this email — that&apos;s almost certainly why login is failing.
                    Purge will delete all of them so you can create a fresh single record.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default UserDiagnosticTool;
